/**
 * Форматирование epoch millis в относительное время ("2 мин. назад", "вчера" и т.д.)
 * с учётом активной локали.
 */

import type { Language } from '@/utils/language';
import { DASH, formatDate } from '@/utils/format';

/** Пороги для единиц времени в миллисекундах */
const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30 * DAY;

/** Единицы в порядке возрастания: первая, чей порог не превышен, и берётся */
const STEPS: ReadonlyArray<{ limit: number; size: number; unit: Intl.RelativeTimeFormatUnit }> = [
	{ limit: MINUTE, size: SECOND, unit: 'second' },
	{ limit: HOUR, size: MINUTE, unit: 'minute' },
	{ limit: DAY, size: HOUR, unit: 'hour' },
	{ limit: MONTH, size: DAY, unit: 'day' },
];

/**
 * Насколько момент может оказаться в будущем и всё ещё считаться «сейчас».
 * Метка чуть впереди текущего времени -- это расхождение часов клиента и
 * сервера, а не событие через полминуты.
 */
const CLOCK_SKEW = MINUTE;

/**
 * Преобразует timestamp (epoch millis) в строку относительного времени:
 * прошлое -- «5 мин. назад», будущее -- «через 2 ч»; длинный стиль -- «5 минут назад»
 * (так подписан последний сигнал сенсоров в макете).
 *
 * Формулировку строит Intl: он сам знает и категории множественного числа
 * русского языка, и идиомы вроде «вчера» вместо «1 день назад». Ручная
 * сборка строк потребовала бы описывать то же самое в словаре.
 */
export function formatRelativeTime(
	epochMs: number | undefined | null,
	locale: Language,
	fallback = DASH,
	timeZone?: string,
	style: Intl.RelativeTimeFormatStyle = 'short',
): string {
	if (!epochMs || epochMs <= 0) return fallback;

	/* Положительная разница -- прошлое, отрицательная -- будущее */
	const elapsed = Date.now() - epochMs;
	const diff = elapsed < 0 && -elapsed < CLOCK_SKEW ? 0 : elapsed;

	/* Дальше месяца в любую сторону относительное время перестаёт быть наглядным */
	const step = STEPS.find(({ limit }) => Math.abs(diff) < limit);
	if (!step) return formatDate(epochMs, locale, fallback, timeZone);

	const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style });
	return format.format(-Math.trunc(diff / step.size), step.unit);
}
